/**
 * SkillBrowser — redesigned with zinc dark design system
 */
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, Chip, Stack,
  InputAdornment, OutlinedInput, Skeleton, LinearProgress
} from '@mui/material';
import { Search, X } from 'lucide-react';

const BG = '#09090b';
const CARD_SX = {
  background: 'rgba(255,255,255,0.025)',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: '12px',
  transition: 'border-color .2s, background .2s',
  '&:hover': { borderColor: 'rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.04)' }
};
const T_PRIMARY = '#fafafa';
const T_SECONDARY = '#a1a1aa';
const T_MUTED = '#71717a';

const DIFF_COLORS = {
  beginner: '#10b981',
  intermediate: '#f59e0b',
  advanced: '#ef4444',
  expert: '#8b5cf6'
};

const getDemandLabel = (demand) => {
  if (demand >= 90) return { label: 'Very High', color: '#10b981' };
  if (demand >= 75) return { label: 'High', color: '#6366f1' };
  if (demand >= 50) return { label: 'Medium', color: '#f59e0b' };
  return { label: 'Low', color: T_MUTED };
};

const SkillCardSkeleton = () => (
  <Card sx={{ ...CARD_SX, p: 0 }}>
    <CardContent sx={{ p: 2.5 }}>
      <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
        <Skeleton variant="circular" width={36} height={36} sx={{ bgcolor: 'rgba(255,255,255,0.05)' }} />
        <Box sx={{ flex: 1 }}>
          <Skeleton width="70%" sx={{ bgcolor: 'rgba(255,255,255,0.05)', mb: 0.5 }} />
          <Skeleton width="40%" sx={{ bgcolor: 'rgba(255,255,255,0.05)' }} />
        </Box>
      </Box>
      <Skeleton height={40} sx={{ bgcolor: 'rgba(255,255,255,0.05)', mb: 1 }} />
      <Skeleton width="60%" sx={{ bgcolor: 'rgba(255,255,255,0.05)' }} />
    </CardContent>
  </Card>
);

const SkillCard = ({ skill }) => {
  const diffColor = DIFF_COLORS[skill.difficulty] || T_MUTED;
  const demand = getDemandLabel(skill.industryDemand || 0);
  const demandPct = Math.min(100, skill.industryDemand || 0);

  return (
    <Card sx={{ ...CARD_SX, p: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ p: 2.5, flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
          <Typography sx={{ fontSize: 28, lineHeight: 1 }}>{skill.icon || '📚'}</Typography>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" fontWeight={700} color={T_PRIMARY} lineHeight={1.3} mb={0.3}>
              {skill.name}
            </Typography>
            <Typography variant="caption" color={T_MUTED}>{skill.category}</Typography>
          </Box>
        </Box>

        {/* Description */}
        <Typography variant="caption" color={T_SECONDARY} sx={{
          flex: 1, display: '-webkit-box', WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical', overflow: 'hidden', mb: 2, lineHeight: 1.5
        }}>
          {skill.description}
        </Typography>

        {/* Meta row */}
        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap" mb={1.5}>
          <Chip
            label={skill.difficulty}
            size="small"
            sx={{ bgcolor: diffColor + '22', color: diffColor, fontSize: 10, fontWeight: 600, height: 20 }}
          />
          <Typography variant="caption" color={T_MUTED}>⏱ {skill.estimatedHours}h</Typography>
        </Stack>

        {/* Demand bar */}
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="caption" color={T_MUTED}>Market Demand</Typography>
            <Typography variant="caption" color={demand.color} fontWeight={600}>{demand.label}</Typography>
          </Box>
          <LinearProgress
            variant="determinate" value={demandPct}
            sx={{ height: 4, borderRadius: 4, bgcolor: 'rgba(255,255,255,0.06)',
              '& .MuiLinearProgress-bar': { bgcolor: demand.color, borderRadius: 4 } }}
          />
        </Box>

        {/* Prerequisites */}
        {skill.prerequisites?.length > 0 && (
          <Box sx={{ mt: 1.5 }}>
            <Typography variant="caption" color={T_MUTED} mb={0.5} display="block">Prerequisites:</Typography>
            <Stack direction="row" gap={0.5} flexWrap="wrap">
              {skill.prerequisites.slice(0, 2).map((p, i) => (
                <Chip key={i} label={p.name || p} size="small"
                  sx={{ bgcolor: 'rgba(255,255,255,0.05)', color: T_MUTED, fontSize: 10, height: 18 }} />
              ))}
              {skill.prerequisites.length > 2 && (
                <Chip label={'+' + (skill.prerequisites.length - 2)} size="small"
                  sx={{ bgcolor: 'rgba(255,255,255,0.05)', color: T_MUTED, fontSize: 10, height: 18 }} />
              )}
            </Stack>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

const CategoryChip = ({ label, active, onClick }) => (
  <Chip
    label={label}
    onClick={onClick}
    sx={{
      cursor: 'pointer',
      bgcolor: active ? '#6366f1' : 'rgba(255,255,255,0.05)',
      color: active ? '#fff' : T_SECONDARY,
      border: active ? 'none' : '1px solid rgba(255,255,255,0.08)',
      fontWeight: active ? 600 : 400,
      '&:hover': { bgcolor: active ? '#4f46e5' : 'rgba(255,255,255,0.09)' },
      transition: 'all .15s'
    }}
  />
);

const SkillBrowser = () => {
  const [skills, setSkills] = useState([]);
  const [filteredSkills, setFilteredSkills] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => { fetchSkills(); fetchCategories(); }, []);
  useEffect(() => { filterSkills(); }, [selectedCategory, searchTerm, skills]);

  const fetchSkills = async () => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('authToken');
      const res = await fetch(
        (process.env.REACT_APP_API_URL || '') + '/api/skills?limit=500',
        token ? { headers: { Authorization: 'Bearer ' + token } } : {}
      );
      if (!res.ok) throw new Error('Failed to fetch skills');
      const data = await res.json();
      setSkills(data.data || []);
      setFilteredSkills(data.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('authToken');
      const res = await fetch(
        (process.env.REACT_APP_API_URL || '') + '/api/skills/meta/categories',
        token ? { headers: { Authorization: 'Bearer ' + token } } : {}
      );
      if (res.ok) {
        const data = await res.json();
        setCategories(['All', ...(data.data || [])]);
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const filterSkills = () => {
    let filtered = skills;
    if (selectedCategory !== 'All') filtered = filtered.filter(s => s.category === selectedCategory);
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      filtered = filtered.filter(s =>
        s.name.toLowerCase().includes(q) || (s.description || '').toLowerCase().includes(q)
      );
    }
    setFilteredSkills(filtered);
  };

  // Group by category for display
  const groupedSkills = React.useMemo(() => {
    if (selectedCategory !== 'All' || searchTerm) return { '': filteredSkills };
    const groups = {};
    filteredSkills.forEach(s => {
      const cat = s.category || 'Other';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(s);
    });
    return groups;
  }, [filteredSkills, selectedCategory, searchTerm]);

  const totalHours = filteredSkills.reduce((sum, s) => sum + (s.estimatedHours || 0), 0);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: BG, color: T_PRIMARY, p: { xs: 2, md: 4 } }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight={800} color={T_PRIMARY} mb={0.5}>
          Skills Library
        </Typography>
        <Typography variant="body1" color={T_MUTED}>
          Explore {skills.length}+ skills to build your learning path
        </Typography>
      </Box>

      {/* Stats bar */}
      <Stack direction="row" gap={3} sx={{ mb: 3, flexWrap: 'wrap' }}>
        {[
          { label: 'Skills', value: filteredSkills.length },
          { label: 'Total Hours', value: Math.round(totalHours) + 'h' },
          { label: 'Categories', value: categories.length > 0 ? categories.length - 1 : 0 },
        ].map(s => (
          <Box key={s.label} sx={{ background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: 3, px: 2.5, py: 1.5 }}>
            <Typography variant="h6" fontWeight={700} color={T_PRIMARY}>{s.value}</Typography>
            <Typography variant="caption" color={T_MUTED}>{s.label}</Typography>
          </Box>
        ))}
      </Stack>

      {/* Search + Category filters */}
      <Box sx={{ mb: 3 }}>
        <OutlinedInput
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          placeholder="Search skills..."
          fullWidth
          startAdornment={<InputAdornment position="start"><Search size={18} color={T_MUTED} /></InputAdornment>}
          endAdornment={searchTerm && (
            <InputAdornment position="end">
              <Box onClick={() => setSearchTerm('')} sx={{ cursor: 'pointer', display: 'flex' }}>
                <X size={16} color={T_MUTED} />
              </Box>
            </InputAdornment>
          )}
          sx={{
            mb: 2, bgcolor: 'rgba(255,255,255,0.03)',
            color: T_PRIMARY, borderRadius: 2,
            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.1)' },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.2)' },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#6366f1' }
          }}
        />

        {categories.length > 0 && (
          <Stack direction="row" gap={1} flexWrap="wrap">
            {categories.map(cat => (
              <CategoryChip key={cat} label={cat} active={selectedCategory === cat}
                onClick={() => setSelectedCategory(cat)} />
            ))}
          </Stack>
        )}
      </Box>

      {/* Error state */}
      {error && (
        <Box sx={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)',
          borderRadius: 2, p: 2, mb: 3, textAlign: 'center' }}>
          <Typography color="#f87171" variant="body2" mb={1}>Error loading skills: {error}</Typography>
          <Box component="button" onClick={fetchSkills}
            sx={{ color: '#f87171', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 1, px: 2, py: 0.5, cursor: 'pointer', bgcolor: 'transparent' }}>
            Retry
          </Box>
        </Box>
      )}

      {/* Skills grid */}
      {loading ? (
        <Grid container spacing={2}>
          {[1,2,3,4,5,6,7,8].map(i => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={i}>
              <SkillCardSkeleton />
            </Grid>
          ))}
        </Grid>
      ) : filteredSkills.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h1" sx={{ mb: 2 }}>🔍</Typography>
          <Typography variant="body1" color={T_MUTED} mb={2}>No skills found matching your criteria.</Typography>
          <Box component="button"
            onClick={() => { setSelectedCategory('All'); setSearchTerm(''); }}
            sx={{ color: '#6366f1', border: '1px solid #6366f133', borderRadius: 1, px: 3, py: 1, cursor: 'pointer', bgcolor: 'transparent' }}>
            Clear Filters
          </Box>
        </Box>
      ) : (
        Object.entries(groupedSkills).map(([cat, catSkills]) => (
          <Box key={cat} sx={{ mb: 4 }}>
            {cat && (
              <Typography variant="h6" fontWeight={600} color={T_SECONDARY} mb={2} sx={{
                display: 'flex', alignItems: 'center', gap: 1,
                '&::after': { content: '""', flex: 1, height: '1px', bgcolor: 'rgba(255,255,255,0.07)', ml: 2 }
              }}>
                {cat}
                <Chip label={catSkills.length} size="small"
                  sx={{ bgcolor: 'rgba(255,255,255,0.05)', color: T_MUTED, fontSize: 10, height: 18 }} />
              </Typography>
            )}
            <Grid container spacing={2}>
              {catSkills.map(skill => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={skill._id}>
                  <SkillCard skill={skill} />
                </Grid>
              ))}
            </Grid>
          </Box>
        ))
      )}
    </Box>
  );
};

export default SkillBrowser;
